import { Card, Form, Grid, Input, Radio, Typography, useMessagedAsyncFn } from "@payping/web-components";

import React from "react";

import { TransferTypeEnum } from "@payping/api/app/api/cashOutTypes";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { isAlphabet } from "@payping/utils/string";
import { SettlementConfiguration } from "@/pages/Settlement/Create";
import { FormInstance } from "@payping/web-components/components/Form/types";
import { setCashOutRequestList } from "@payping/redux/actions";
import { RequestStatusEnum, requestApis } from "@payping/api";
import { useAppDispatch } from "@/utils";
import { useParams } from "react-router-dom";
import { useAsync } from "react-use";

export interface Props {
  form: FormInstance<SettlementConfiguration>;
  next: (requestCode?: string) => void;
  setRequestTitle: React.Dispatch<React.SetStateAction<string>>;
  setRequestDescription: React.Dispatch<React.SetStateAction<string>>;
  setTransferType: React.Dispatch<React.SetStateAction<TransferTypeEnum>>;
  setRequestStatus: React.Dispatch<React.SetStateAction<RequestStatusEnum>>;
  setAmountStepOne: React.Dispatch<React.SetStateAction<number>>;
  setLoadingStepOne: React.Dispatch<React.SetStateAction<boolean>>;
  transferType: TransferTypeEnum;
}

const FirstStep: React.FC<Props> = ({
  form,
  next,
  setRequestTitle,
  transferType,
  setTransferType,
  setRequestDescription,
  setLoadingStepOne,
  setRequestStatus,
  setAmountStepOne,
}) => {
  const { code } = useParams<{ code: string }>() as { code: string };
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation();
  const { lg } = Grid.useBreakpoint();

  useAsync(async () => {
    const response = await requestApis.getRequestDetailsWithRequestCodeWithCancellationTokenGet(code);

    setRequestTitle(response.title || "");
    setRequestDescription(response.description || "");
    setTransferType(response.transferType || TransferTypeEnum.AUTO);
    setRequestStatus(response.status || RequestStatusEnum.Incomplete);
    setAmountStepOne(response.totalAmount || 0);

    await dispatch(setCashOutRequestList({ code, settlements: response.settlements }));
  }, [code]);

  const [{ loading }, onFinish] = useMessagedAsyncFn(
    async (values: SettlementConfiguration) => {
      setLoadingStepOne(true);
      const transType = values.transferType ? values.transferType : TransferTypeEnum.AUTO;

      if (code) {
        await requestApis.updateRequestWithModelWithCancellationTokenPost({
          code,
          title: values.title,
          description: values.description,
          transferType: transType,
        });
        next(code);
      } else {
        await dispatch(
          setCashOutRequestList({
            code: "",
            errors: [],
            settlements: [],
          }),
        );

        const response = await requestApis.createBatchRequestWithExcelWithFileWithModelPost(
          values.title,
          undefined,
          values.description,
          undefined,
          undefined,
          transType,
        );
        next(response.code);
      }

      // await dispatch(setCashOutRequestList(response));

      setLoadingStepOne(false);
    },
    [dispatch, next],
    undefined,
    {
      useProgress: true,
    },
  );

  return (
    <Form<SettlementConfiguration>
      onFinish={onFinish}
      initialValues={{ transferType }}
      onValuesChange={(changedValues) => {
        if ("title" in changedValues) {
          setRequestTitle(changedValues.title);
        }
        if ("transferType" in changedValues) {
          setTransferType(changedValues.transferType);
        }
        if ("description" in changedValues) {
          setRequestDescription(changedValues.description);
        }
      }}
      form={form}
      className="tw-w-full"
      layout={"vertical"}
    >
      <Card.Ant
        loading={loading}
        title={
          <Typography.Title type={"secondary"} level={5}>
            {t("settlement.create.fileSetting")}
          </Typography.Title>
        }
        className={!lg ? "tw-mb-[240px]" : undefined}
      >
        <Form.Item label={t("settlement.fileName")} name={"title"} rules={[{ required: true }]} wrapperCol={{ lg: 10 }}>
          <Input className={"tw-w-full"} />
        </Form.Item>
        <Form.Item
          wrapperCol={{ lg: 10 }}
          name={"description"}
          label={t("description")}
          rules={[
            {
              validator(_, value) {
                if (!isAlphabet(value, { withNumber: true }))
                  return Promise.reject(new Error(t("settlement.error.descriptionException")));
                if (value && value.length > 50) return Promise.reject(new Error(t("settlement.error.description")));
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder={t("settlement.fileDescription")} />
        </Form.Item>
        <Form.Item label={t("settlement.create.settlementType")} name={"transferType"} wrapperCol={{ lg: 10 }}>
          <Radio.Group className="tw-w-full">
            <Radio.Button value={TransferTypeEnum.AUTO}>{t("settlement.type.auto")}</Radio.Button>
            {/* <Radio.Button value={TransferTypeEnum.NORMAL}>{t("settlement.type.acountToAcount")}</Radio.Button>
            <Radio.Button value={TransferTypeEnum.ACH}>{t("settlement.type.paya")}</Radio.Button> */}
          </Radio.Group>
        </Form.Item>
      </Card.Ant>
    </Form>
  );
};

export default FirstStep;
